/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "@mui/styled-engine-sc";
import get from "lodash-es/get";
import isEqual from "lodash-es/isEqual";
import { StyledRadioGroup, StyledFormControl, StyledToggleButtonGroup } from "@hcl-commerce-store-sdk/react-component";
import { StyledBox, StyledButton, StyledSwatch, StyledToggleButton, StyledTypography } from "../../styled-mui";
import useMediaQuery from "@mui/material/useMediaQuery";
import useTheme from "@mui/material/styles/useTheme";
import storeUtil from "../../utils/storeUtil";
import { ATTR_IDENTIFIER } from "../../constants/catalog";
import AsyncCall from "../../_foundation/gtm/async.service";
import { CLICK } from "../../_foundation/constants/gtm";
import { useTranslation } from "react-i18next";
import { ImageLayout } from "../../components/elements/image";
import { Tooltip } from "@mui/material";

function RadioGroupWrapper({
  id,
  name,
  onChangeHandler,
  values,
  useSwatches,
  className,
  productDetailsAny,
  skusAsAttrs,
  isB2B,
  currentSelection,
  rowNo,
  variant = "default",
  ...props
}: any) {
  let defaultSelected = "";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const currentSelectedAttributes = get(currentSelection, "selectedAttributes", {});
  defaultSelected = currentSelectedAttributes[id] ?? "";
  if (defaultSelected) {
    defaultSelected = Array.isArray(defaultSelected) ? currentSelectedAttributes[id][0] : currentSelectedAttributes[id];
  }
  // Detect dark colors based on color label (WORKS with your data)
  const isDarkColorFromLabel = (label?: string) => {
    if (!label) return false;

    const value = label.toLowerCase();

    if (value.includes("gris 7016 - blanc 9016")) {
      return false;
    }

    return (
      value.includes("noir") ||
      value.includes("anthracite") ||
      value.includes("foncé") ||
      value.includes("fonce") ||
      value.includes("sombre") ||
      value.includes("dark") ||
      value.includes("charbon") ||
      value.includes("brun") ||
      value.includes("chêne foncé") ||
      value.includes("wengé") ||
      value.includes("wenge") ||
      value.includes("gris") ||
      value.includes("noyer") ||
      value.includes("caviar") ||
      value.includes("bleu") ||
      value.includes("olive") ||
      value.includes("terra")
    );
  };

  const getMaxItem = () => {
    if (
      id.indexOf(ATTR_IDENTIFIER.LARGEUR) > -1 ||
      id.indexOf(ATTR_IDENTIFIER.LONGUEUR) > -1 ||
      id.indexOf(ATTR_IDENTIFIER.HAUTEUR) > -1 ||
      id.indexOf(ATTR_IDENTIFIER.PROFONDEUR) > -1
    ) {
      return isMobile ? 5 : 6;
    } else if (id.indexOf(ATTR_IDENTIFIER.FINITION) > -1) {
      return 5;
    } else {
      return isMobile ? 7 : 9;
    }
  };

  const [unfold, setUnfold] = useState(getMaxItem() < values.findIndex((v) => v.value === defaultSelected));

  // from current selection's attributes, discard the current name/value pair
  const otherAttrs = Object.keys(currentSelectedAttributes)
    .filter((k) => k !== id)
    .reduce((m, v) => {
      m[v] = currentSelectedAttributes[v];
      return m;
    }, {});

  const [value, setValue] = React.useState(defaultSelected);

  const handleChange = (event: any) => {
    setValue(event.target.value);
    onChangeHandler(id, event.target.value);
  };

  const handleClick = (optionValue: string) => {
    setValue(optionValue);
    onChangeHandler(id, optionValue);
  };
  // const MoreAttributes = useMemo(
  //   () => () => {
  //     let label = "";
  //     if (
  //       id.indexOf(ATTR_IDENTIFIER.LARGEUR) > -1 ||
  //       id.indexOf(ATTR_IDENTIFIER.LONGUEUR) > -1 ||
  //       id.indexOf(ATTR_IDENTIFIER.HAUTEUR) > -1 ||
  //       id.indexOf(ATTR_IDENTIFIER.PROFONDEUR) > -1
  //     ) {
  //       label = `${t("ProductAttributes.More.Dimension")}s`;
  //     } else if (id.indexOf(ATTR_IDENTIFIER.FINITION) > -1) {
  //       label = t("ProductAttributes.More.Finition");
  //     } else {
  //       label = t("ProductAttributes.More.Couleur");
  //     }
  //     const handleClickShowMore = () => {
  //       AsyncCall.sendPageClickCtaEvent({
  //         event: CLICK.PRODUCT_DETAIL,
  //         btn_label: !unfold ? label : t("ProductAttributes.Less"),
  //         product_id: currentSelection.sku[0].partNumber,
  //         product_name: currentSelection.sku[0].name,
  //       });
  //       setUnfold(!unfold);
  //     };

  //     return values?.length > getMaxItem() ? (
  //       <StyledButton
  //         variant="text"
  //         className="link-hover-animated product-information-link-more"
  //         color="secondary"
  //         onClick={handleClickShowMore}
  //         sx={{
  //           textTransform: "none",
  //           fontWeight: 400,
  //           textUnderlineOffset: "3px",
  //           marginTop: "20px",
  //         }}>
  //         <span style={{ fontSize: "15px" }}>{!unfold ? label : t("ProductAttributes.Less")} </span>
  //       </StyledButton>
  //     ) : (
  //       <></>
  //     );
  //   },
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [unfold]
  // );

  /* update rest of the values based on possible values because currentSelection doesn't update if combination is not possible */
  useEffect(() => {
    setValue(currentSelection.selectedAttributes[id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection]);

  const orderValues = values?.sort((a, b) => {
    if (isNaN(a.value) && isNaN(b.value)) {
      return a.value < b.value ? -1 : 1;
    } else {
      return parseInt(a.value) < parseInt(b.value) ? -1 : 1;
    }
  });

  // const getTooltipTitle = (value?: string) => {
  //   if (!value) return "";

  //   const words = value.split(" ");
  //   if (words.length <= 2) return value;

  //   return (
  //     <>
  //       {words.slice(0, 2).join(" ")}
  //       <br />
  //       {words.slice(2).join(" ")}
  //     </>
  //   );
  // };

  return (
    <StyledFormControl component="fieldset" className={useSwatches ? `${className} swatch-group` : className}>
      {useSwatches ? (
        <StyledRadioGroup
          aria-label={name}
          name={name}
          value={value}
          onChange={handleChange}
          sx={{ flexDirection: "column !important", gap: "15px !important" }}>
          {orderValues?.map((v, index) => {
            const isSelected = defaultSelected === v.value;
            const useWhiteTick = isDarkColorFromLabel(v?.value);

            return (
              <StyledBox
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px !important",
                  marginRight: "0",
                }}>
                {/* Swatch wrapper */}
                {/* <Tooltip
                  title={getTooltipTitle(v?.value)}
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#2C5F7C",
                        fontSize: "11.5px",
                        padding: "6px 10px",
                        borderRadius: "6px",
                        lineHeight: "16px",
                        textAlign: "center",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#2C5F7C",
                      },
                    },
                  }}>
                  <StyledBox
                    sx={{
                      position: "relative",
                      width: "45px",
                      height: "45px",
                      borderRadius: "8px",
                      border: "2px solid #E0E0E0",
                      cursor: "pointer",
                    }}
                    onClick={() => handleClick(v.value)}>
                    <StyledSwatch
                      style={{
                        backgroundImage: `url("${storeUtil.getUrlPicture(v.image1)}")`,
                        backgroundSize: "contain",
                        width: "100%",
                        height: "100%",
                        borderRadius: "8px",
                      }}
                      selected={isSelected}
                      size={name.toUpperCase() === ATTR_IDENTIFIER.COULEUR ? "medium" : "big"}
                      data-testid={`radio-group-wrapper-${v.identifier.toLowerCase()}-swatch`}
                    />

                    {isSelected && (
                      <StyledBox
                        sx={{
                          position: "absolute",
                          top: "6px",
                          left: "12px",
                          width: "20px",
                          height: "31px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          filter: useWhiteTick ? "invert(1) brightness(2)" : "none",
                        }}>
                        <ImageLayout src={"/images/pdp/tick.svg"} alt={"Lapeyre"} width={"18px"} height={"18px"} />
                      </StyledBox>
                    )}
                  </StyledBox>
                </Tooltip> */}

                <StyledBox
                  sx={{
                    position: "relative",
                    width: "50px",
                    height: "50px",
                    borderRadius: "8px",
                    border: "2px solid #E0E0E0",
                    cursor: "pointer",
                  }}
                  onClick={() => handleClick(v.value)}>
                  <StyledSwatch
                    style={{
                      backgroundImage: `url("${storeUtil.getUrlPicture(v.image1)}")`,
                      backgroundSize: "contain",
                      width: "100%",
                      height: "100%",
                      borderRadius: "8px",
                    }}
                    selected={isSelected}
                    size={name.toUpperCase() === ATTR_IDENTIFIER.COULEUR ? "medium" : "big"}
                    data-testid={`radio-group-wrapper-${v.identifier.toLowerCase()}-swatch`}
                  />

                  {isSelected && (
                    <StyledBox
                      sx={{
                        position: "absolute",
                        top: "9px",
                        left: "15px",
                        width: "20px",
                        height: "31px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        filter: useWhiteTick ? "invert(1) brightness(2)" : "none",
                      }}>
                      <ImageLayout src={"/images/pdp/tick.svg"} alt={"Lapeyre"} width={"18px"} height={"18px"} />
                    </StyledBox>
                  )}
                </StyledBox>

                <StyledTypography
                  sx={{
                    color: "#1A1A1A",
                    textAlign: "center",
                    fontFamily: "Montserrat",
                    fontSize: "15px",
                    fontWeight: 700,
                    lineHeight: "24px",
                  }}>
                  {v?.value}
                </StyledTypography>
              </StyledBox>
            );
          })}
        </StyledRadioGroup>
      ) : (
        <StyledToggleButtonGroup
          value={value}
          exclusive
          onChange={handleChange}
          aria-label={name}
          className="bottom-margin-1 flex-wrap aaaaa"
          sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
          {values?.map((v, index) => {
            // for B2C scenarios, if this attribute's value isn't in current selection, check to see
            //   if there exists such a SKU -- if it doesn't we will disable this attribute value, e.g.,
            //
            //   currentSku = { Color: "blue", Size: "48x48x48" }
            //   currentAttr = { Size: "52x52x52" };
            //
            // merge with `otherAttrs`, i.e., { Color: "blue" } --> { Color: "blue", Size: "52x52x52" }
            //   then look in skusAsAttrs array to see if such a sku exists
            //
            // if it exists, keep this attr-value enabled, otherwise disable it because no such SKU exists
            const label = storeUtil.parseFloatDecimal(v.value);
            const findSku =
              !isB2B &&
              !useSwatches &&
              !Array.isArray(currentSelectedAttributes[id]) &&
              v.value !== currentSelectedAttributes[id];
            const skuToFind = findSku ? { ...otherAttrs, [id]: v.value } : null;
            const skuExists = findSku ? skusAsAttrs.find((sku) => isEqual(sku, skuToFind)) : null;
            return (
              <StyledToggleButton
                key={index}
                value={v.value}
                aria-label={v.value}
                disabled={rowNo !== 0 && findSku && !skuExists}
                sx={
                  variant === "pill"
                    ? {
                        height: { xs: "50px", md: "54px" },
                        width: "100%",
                        p: { xs: "8px !important", md: "14px !important" },
                        borderRadius: "8px",
                        backgroundColor: value === v.value ? "#5D89A2" : "#FFF",
                        color: value === v.value ? "#FFF" : "#1A1A1A",
                        fontSize: { xs: "12px", md: "14px" },
                        textTransform: "none",
                        margin: "0 !important",
                        gridColumn: label.length > 12 ? "span 2" : "span 1",

                        "&:hover": {
                          backgroundColor: "#F5F5F5",
                        },

                        "&.MuiToggleButton-root": {
                          border: "2px solid #E0E0E0",
                          fontWeight: 700,
                        },

                        "&.Mui-selected, &.Mui-selected.MuiToggleButton-root": {
                          backgroundColor: "#5D89A2 !important",
                          borderColor: "#527C93 !important",
                          color: "#FFFFFF !important",
                          fontWeight: "400 !important",
                        },

                        "&.Mui-selected:hover": {
                          backgroundColor: "#5D89A2 !important",
                        },
                      }
                    : {}
                }
                className={`${
                  index >= getMaxItem() && !unfold && "hide"
                } bottom-margin-2 product-information-toggle-button`}>
                {storeUtil.parseFloatDecimal(v.value)}
              </StyledToggleButton>
            );
          })}
        </StyledToggleButtonGroup>
      )}
      {/* <MoreAttributes /> */}
    </StyledFormControl>
  );
}

RadioGroupWrapper.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChangeHandler: PropTypes.any.isRequired,
  values: PropTypes.any.isRequired,
  useSwatches: PropTypes.bool,
};

const StyledSwatchRadioGroup = styled(({ ...props }) => <RadioGroupWrapper {...props} />)`
  ${({ theme }) => `
    &.swatch-group .MuiFormGroup-root {
      flex-direction: row;
    }
  `}
`;

export { StyledSwatchRadioGroup };
